<template>
  <Modal :visible="visible" @close="$emit('close')">
    <template #header>
      <p class="modalTitle">Reserve Meeting Room</p>
    </template>
    <template #body>
      <div class="form-row">
        <label>Date</label>
        <span>{{ formattedDate }}</span>
      </div>

      <form @submit.prevent="submitReservation">
        <div class="form-row">
          <label>Meeting Title</label>
          <input v-model="title" required />
        </div>

        <div class="form-row">
          <label>Start Time</label>
          <select v-model="startTime">
            <option v-for="t in selectableTimes" :key="t">{{ t }}</option>
          </select>
        </div>

        <div class="form-row">
            <label>End Time</label>
            <select v-model="endTime">
                <option v-for="t in endTimeOptions" :key="t" :value="t">{{ t }}</option>
            </select>
            <p v-if="endTimeOptions.length === 0 && startTime" class="hint">
                No contiguous time is available after the selected start time.
            </p>
        </div>
      </form>
    </template>
    <template #footer>
      <div class="btnWrap">
        <button type="button" @click="$emit('close')" class="submit-button cancelBtn">Cancel</button>
        <button type="submit" @click="submitReservation" class="submit-button">Reserve</button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import api from "@/services/api";
import Modal from "@/components/Modal.vue";

const props = defineProps({
  date: String,
  time: String,
  roomId: Number,
  visible: { type: Boolean, required: true },
});

const emit = defineEmits(["reservation-created"]);

const formattedDate = computed(() => {
  if (!props.date) return "";
  const [year, month, day] = props.date.split("-");
  return `${day}/${month}/${year}`;
});

const title = ref("");
const startTime = ref(props.time);
const endTime = ref("");
const endTimeOptions = ref([]);
const reservations = ref([]);

const selectableTimes = computed(() => {
  const list = [];
  for (let hour = 8; hour <= 19; hour++) {
    for (let min = 0; min < 60; min += 15) {
      const hh = hour.toString().padStart(2, "0");
      const mm = min.toString().padStart(2, "0");
      list.push(`${hh}:${mm}`);
    }
  }
  return list;
});

const selectableTimesAfterStart = computed(() => {
  const startIdx = selectableTimes.value.indexOf(startTime.value);
  return selectableTimes.value.slice(startIdx + 1);
});

// 当日・当該ルームの予約だけに絞る
const dayReservationsForRoom = computed(() => {
    return reservations.value.filter((r) => {
        const roomOk = String(r.room) === String(props.roomId);
        const dateStr =
            typeof r.start_datetime === "string"
                ? r.start_datetime.slice(0, 10)
                : (r.date || ""); // 後方互換: r.date があるケース
        const dateOk = dateStr === props.date;
        return roomOk && dateOk;
    });
});

function toMinutes(hhmm) {
    if (!hhmm) return 0;
    const [h, m] = String(hhmm).split(":").map(Number);
    return h * 60 + (isNaN(m) ? 0 : m);
}

function toHHMM(mins) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0");
}

function reservationSpanMinutes(r) {
    const startStr =
        r.start
            || (typeof r.start_datetime === "string" ? r.start_datetime.slice(11, 16) : "");
    const endStr =
        r.end
            || (typeof r.end_datetime === "string" ? r.end_datetime.slice(11, 16) : "");
    return [toMinutes(startStr), toMinutes(endStr)];
}

function isOccupiedAt(minFromMidnight) {
    return dayReservationsForRoom.value.some((r) => {
        const [s, e] = reservationSpanMinutes(r);
        return minFromMidnight >= s && minFromMidnight < e;
    });
}

// 指定分(min from midnight)のスロット開始が既存予約に含まれているか
function nextBusyStartAfter(startM, closeM, step = 15) {
    const starts = dayReservationsForRoom.value
        .map((r) => reservationSpanMinutes(r)[0])
        .filter((s) => s >= startM + step && s <= closeM);
    return starts.length ? Math.min(...starts) : closeM;
}

// selectableTimes の範囲を営業時間として扱い、startTime から連続空きを走査して endTimeOptions を構築
function recomputeEndOptions() {
    endTimeOptions.value = [];
    endTime.value = "";

    if (!startTime.value) return;
    const step = 15; // 15分刻み

    // 営業開始=最初の候補、営業終了=最後の候補+15分
    const openM  = toMinutes(selectableTimes.value[0] || "08:00");
    const last   = selectableTimes.value[selectableTimes.value.length - 1] || "20:00";
    const closeM = toMinutes(last) + step;

    const startM = toMinutes(startTime.value);
    if (startM < openM || startM >= closeM) return;
    if (isOccupiedAt(startM)) return; // 開始が既存予約と重複していたら候補なし

    const blockAt = nextBusyStartAfter(startM, closeM, step);
    for (let cur = startM + step; cur <= Math.min(blockAt, closeM); cur += step) {

        endTimeOptions.value.push(toHHMM(cur));
    }

    // UX: デフォルトは開始から1時間後、なければ取れる最大の終了時刻
    if (endTimeOptions.value.length > 0) {
        const target = startM + 60; // 1 hour later
        const candidate = endTimeOptions.value.find((opt) => toMinutes(opt) >= target);
        endTime.value = candidate || endTimeOptions.value[endTimeOptions.value.length - 1];
    }
}

// 予約一覧の取得（バックエンドの一覧APIに合わせて調整可）
async function fetchReservations() {
    try {
        const res = await api.get("/meeting/reservations/");
        reservations.value = Array.isArray(res.data) ? res.data : (res.data?.results || []);
    } catch (e) {
        // 取得失敗しても UI は動く（候補は full になるだけ）
        console.error("Failed to fetch meeting reservations:", e);
        reservations.value = [];
    } finally {
        recomputeEndOptions();
    }
}

watch(props, () => {
  startTime.value = props.time;
  recomputeEndOptions();
});

// 最新の予約状況をモーダル表示時に取得し直す（キャンセル直後の空きも反映）
watch(
  () => props.visible,
  (isVisible) => {
    if (isVisible) {
      // 表示のたびに初期値をリセットして再計算
      startTime.value = props.time;
      fetchReservations();
    }
  }
);

async function submitReservation() {
  const startDatetime = `${props.date}T${startTime.value}:00`;
  const endDatetime = `${props.date}T${endTime.value}:00`;

  console.log(startDatetime, endDatetime, props.roomId, title.value);

  try {
    await api.post("/meeting/reservations/", {
      // axios.post を api.post に変更
      room: props.roomId,
      title: title.value,
      start_datetime: startDatetime,
      end_datetime: endDatetime,
    });

    emit("reservation-created");
  } catch (error) {
        console.error("Reservation failed:", error);
        let message = "Reservation failed. Please try again.";
        if (error.response) {
            const status = error.response.status;
            const detail = error.response.data?.detail;
            if (status === 400) {
                message = detail || "Invalid request. Please check the date and time.";
            } else if (status === 403) {
                message = detail || "You do not have permission to reserve this room.";
            } else if (status === 409) {
                message = detail || "The room is already reserved for this time slot.";
            } else {
                message = detail || "Reservation failed due to an unexpected error.";
            }
        } else {
            message = "Network error. Please check your connection.";
        }
        alert(message);
    }
}

// ユーザー操作に応じて候補を再計算
watch(startTime, () => {
    recomputeEndOptions();
});

onMounted(() => {
    fetchReservations();
    // 初期 props.time が入っている場合に備え
    recomputeEndOptions();
});
</script>

<style>
    .modal-header, .modal-footer{
        padding: 0!important;
        border-bottom: none !important;
    }
    .modal-container{
        padding: 2rem;
    }
</style>
<style scoped>
    .form-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 1rem; /* Added for top margin */

        & input, & select {
            padding: 1rem;
        }
    }
    .error {
        color: red;
        margin-top: 8px;
    }
    .single-reserve,
    .bulk-reserve {
        margin-top: 12px;
    }
    .btnWrap {
        display: flex;
        justify-content: center;
        margin-top: 1rem;
    }

    .modalTitle{
        font-size: 20px;
        color: var(--blue);
        font-weight: 600;
        text-align: center;
        margin:0;
        & span{
            font-size: 0.9em;
        }
    }
    .guestNameWrap>input {
        margin-top: 1rem;
        width: calc(100% - 2rem);
        padding: 1rem;
    }
    .submit-button {
        background: var(--grad90);
        width: 100%;
        max-width: 300px;
        padding: 1rem;
        border: none;
        color: white;
        border-radius: 10px;
        margin: 0;
        display: block;

        &.cancelBtn{
            background: none;
            border: 1px solid var(--blue);
            color: var(--blue);
            max-width: 150px;
            margin-right: 1rem;
        }

        .submit-button:disabled {
            background: #cccccc;
            cursor: inherit;
        }
    }

    /* トグルスイッチのスタイル */
    .rm-toggle-wrapper {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 1rem;
        padding-top: 1rem;
        width: 100%;
        border-top: 1px dashed var(--midLightBlue);
        & span {
            color: var(--blue);
            font-size: 14px;
            font-weight: 600;
        }
    }
    .rm-toggle-label {
        position: relative;
        display: block;
        width: 60px;
        height: 29px;
        background: #ccc;
        border-radius: 23px;
        cursor: pointer;
        transition: background 0.2s;

        &:after {
            content: "OFF";
            position: absolute;
            top: 3px;
            left: 3px;
            padding: 3px 6px;
            background: white;
            border-radius: 100px;
            transition: transform 0.2s;
            font-size: 14px;
            color: gray;
            font-weight: 600;
        }
    }
    .rm-toggle-input {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
        z-index: 1;

        &:checked + .rm-toggle-label {
            background: var(--blue);
        }
        &:checked + .rm-toggle-label:after {
            content: "ON";
            color: var(--blue);
            transform: translateX(20px);
        }
    }
</style>
